import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import ChatTabs from "./ChatTabs";
import { useAuth } from "../../context/AuthContext";
import { useLanguage } from "../../context/LanguageContext";
import { Star, Search, X } from "lucide-react";
import { MOODS } from "../../lib/constants";

export default function ChatSidebar({
  activeTab,
  setActiveTab,
  selectedGuide,
  setSelectedGuide,
  chatList = [],
  suggestedList = [],
  onRandom,
  onClearConversation,
  onCloseMobile,
}) {
  const { user } = useAuth();
  const { t } = useLanguage();
  const [searchTerm, setSearchTerm] = useState("");

  // ⭐️ Favorites Logic (Local Persistence)
  const [favorites, setFavorites] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem("chatFavorites")) || [];
    } catch { return []; }
  });

  useEffect(() => {
    localStorage.setItem("chatFavorites", JSON.stringify(favorites));
  }, [favorites]);

  const toggleFavorite = (id, e) => {
    e.stopPropagation(); // Prevent opening chat
    setFavorites(prev =>
      prev.includes(id) ? prev.filter(fid => fid !== id) : [...prev, id]
    );
  };

  // Determine Source List based on Tab
  let currentList = chatList;
  if (activeTab === "Suggested") {
    // FILTER: Remove duplicated providers who are already in the chat list
    const chatProviderIds = new Set(chatList.map(c => c.id));
    currentList = suggestedList.filter(p => !chatProviderIds.has(p.id));
  }

  // Filter List based on Tab Logic
  const filteredList = currentList.filter((p) => {
    // 1. Hide other Providers if I am a Provider
    if (user?.role === "PROVIDER" && p.role === "PROVIDER") return false;

    // 2. Filter by Search Term
    if (searchTerm && !p.name.toLowerCase().includes(searchTerm.toLowerCase())) {
      return false;
    }

    // 3. Filter by Tab
    if (activeTab === "Favourites") {
      return favorites.includes(p.id) && !p.isArchived;
    }
    if (activeTab === "Archived") {
      return p.isArchived;
    }
    if (activeTab === "Recent") {
      return !p.isArchived;
    }
    return !p.isArchived;
  });

  return (
    <aside className="h-full relative flex flex-col p-4 bg-white dark:bg-gray-900 border-r border-gray-100 dark:border-gray-800 overflow-hidden">
      {/* Background Watermark (Welcome Back) - only show if there are chats in Recent */}
      {activeTab === "Recent" && chatList.length > 0 && !searchTerm && (
        <motion.div 
          initial={{ opacity: 0 }}
          animate={{ opacity: 0.08 }} // Very subtle watermark
          className="absolute inset-0 z-0 flex flex-col items-center justify-center pointer-events-none mt-20"
        >
          <h1 className="text-3xl sm:text-4xl font-display font-bold text-gray-900 dark:text-white mb-2">{t('chat_welcome_head')}</h1>
          <p className="text-sm sm:text-base text-center font-serif text-gray-800 dark:text-gray-200 max-w-[220px]">
            {user?.role === "PROVIDER" ? t('chat_welcome_body_guide') : t('chat_welcome_body')}
          </p>
        </motion.div>
      )}

      {/* Header / Tabs */}
      <div className="shrink-0 mb-4 space-y-4 relative z-20">
        {/* Mobile Header */}
        <div className="md:hidden flex items-center justify-between pt-2">
          <h2 className="text-xl font-display font-bold text-gray-900 dark:text-gray-100">{t('nav_chats')}</h2>
        </div>

        {/* Search Bar */}
        <div className="relative group">
          <input
            type="text"
            placeholder={t('chat_search_placeholder')}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-9 pr-4 py-2 bg-gray-100/50 dark:bg-gray-800/50 backdrop-blur-md rounded-xl text-sm border border-transparent focus:border-brand-blue/30 focus:ring-4 focus:ring-brand-blue/10 focus:bg-white dark:focus:bg-gray-800 transition-all duration-300 placeholder:text-gray-400"
          />
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 group-focus-within:text-brand-blue transition-colors" />
        </div>

        <ChatTabs
          activeTab={activeTab}
          setActiveTab={setActiveTab}
          tabs={user?.role === "PROVIDER" ? ["Recent", "Favourites", "Archived"] : ["Recent", "Suggested", "Favourites", "Archived"]}
        />
      </div>

      {/* List */}
      <div className="flex-1 overflow-y-auto min-h-0 custom-scrollbar pr-1 -mr-2 relative z-10">
        {activeTab !== "Categories" ? (
          <div className="space-y-1 pb-20"> {/* pb-20 added for mobile nav clearance */}
            {filteredList.length === 0 && (
              <div className="flex flex-col items-center justify-center min-h-[300px] text-center p-6">
                {activeTab === "Recent" && !searchTerm ? (
                  <motion.div 
                     initial={{ opacity: 0, y: 10 }}
                     animate={{ opacity: 1, y: 0 }}
                     className="space-y-5"
                  >
                    <h2 className="text-2xl font-display font-normal text-gray-900 dark:text-white">{t('chat_welcome_namaste')}</h2>
                    <p className="text-gray-500 dark:text-gray-400 text-sm leading-relaxed max-w-[240px] mx-auto">
                      {user?.role === "PROVIDER" ? t('chat_welcome_time_guide') : t('chat_welcome_time')}
                    </p>
                    <p className="text-gray-500 dark:text-gray-400 text-sm leading-relaxed max-w-[240px] mx-auto">
                      {user?.role === "PROVIDER" ? t('dash_recent_empty_sub_provider') : t('dash_recent_empty_sub')}
                    </p>
                    <button 
                      onClick={() => setActiveTab("Suggested")}
                      className="mt-6 px-6 py-2.5 bg-brand-blue text-white rounded-full text-sm font-medium hover:bg-blue-600 transition shadow-lg shadow-blue-500/20 active:scale-95"
                    >
                      {t('gateway_cta')}
                    </button>
                  </motion.div>
                ) : (
                  <span className="text-gray-400 dark:text-gray-500 text-sm italic leading-relaxed">
                    {searchTerm ? t('chat_empty_matches') :
                      activeTab === "Favourites" ? t('chat_empty_favs') :
                        activeTab === "Archived" ? t('chat_empty_archived') : t('chat_empty_conv')}
                  </span>
                )}
              </div>
            )}

            {filteredList.map((person) => {
              const isSelected = selectedGuide?.id === person.id;
              const isFav = favorites.includes(person.id);

              const otherMoodName = person.otherUser?.currentMood || person.otherUser?.moods?.[0]?.mood || "neutral";
              const userMood = MOODS.find(m => m.id === otherMoodName || m.label === otherMoodName) || MOODS.find(m => m.id === "neutral");

              return (
                <button
                  key={person.id}
                  onClick={() => setSelectedGuide(person)}
                  className={`w-full text-left p-3 rounded-2xl transition-all duration-200 flex items-center gap-3 group relative overflow-visible ${!isSelected ? "bg-white/40 dark:bg-gray-900/40 backdrop-blur-sm border border-transparent hover:border-gray-200 dark:hover:border-gray-800" : ""}`}
                >
                  {/* Fluid Active Background (Frosted Glass) */}
                  {isSelected && (
                    <motion.div
                      layoutId="activeChatBg"
                      className="absolute inset-0 bg-white dark:bg-gray-800/80 backdrop-blur-md rounded-2xl border border-gray-200 dark:border-gray-700 shadow-sm"
                      initial={false}
                      transition={{ type: "spring", stiffness: 500, damping: 35 }}
                    />
                  )}

                  {/* Avatar / Dynamic with Mood Ring */}
                  <div className="relative shrink-0">
                    {/* The avatar itself */}
                    <div className={`
                          relative z-10 w-12 h-12 rounded-full flex items-center justify-center font-bold text-lg overflow-hidden transition-all duration-300 shadow-sm
                          ${isSelected ? "scale-105 shadow-brand-blue/20" : "bg-gray-50 dark:bg-gray-800 text-gray-500"}
                      `}>
                      {person.otherUser?.avatar ? (
                        <img src={person.otherUser.avatar} alt={person.name} className="w-full h-full object-cover" />
                      ) : (
                        <span className="opacity-80">{(person.name || "U")[0].toUpperCase()}</span>
                      )}
                    </div>

                    {/* The Mood Ring Wrapper (Radiant Glow) - Safety Check: userMood might be null if MOODS is missing */}
                    {userMood && (
                      <div
                        className={`absolute -inset-[3px] rounded-full border-[2.5px] ${userMood.border} opacity-80 ${isSelected ? 'scale-105' : ''} pointer-events-none transition-all duration-500`}
                        style={{
                          boxShadow: isSelected ? `0 0 12px ${userMood.id === 'neutral' ? 'rgba(156, 163, 175, 0.3)' : `var(--mood-glow, rgba(99, 102, 241, 0.4))`}` : 'none',
                          filter: `drop-shadow(0 0 2px ${userMood.id === 'neutral' ? 'transparent' : 'currentColor'})`
                        }}
                      />
                    )}
                  </div>

                  <div className="flex-1 min-w-0 z-10">
                    <div className="flex items-center justify-between">
                      <span className={`font-semibold truncate text-[15px] transition-colors duration-200 ${isSelected ? "text-brand-blue dark:text-blue-400" : "text-gray-800 dark:text-gray-200"}`}>
                        {person.name}
                      </span>
                      {/* Favorite Button (Visible on Hover or if active) */}
                      <div
                        onClick={(e) => toggleFavorite(person.id, e)}
                        className={`p-1.5 rounded-full transition-colors 
                                ${isFav
                            ? "text-yellow-400"
                            : "text-gray-300 hover:text-yellow-400 opacity-0 group-hover:opacity-100"
                          }
                            `}
                      >
                        <Star className={`w-4 h-4 ${isFav ? "fill-current" : ""}`} />
                      </div>
                    </div>
                    <p className={`text-xs truncate mt-0.5 transition-colors duration-200 ${isSelected ? "text-brand-blue/80 dark:text-blue-300/80" : "text-gray-500"}`}>
                      {person.role === "PROVIDER" ? t('chat_role_guide') : t('chat_role_seeker')}
                    </p>
                  </div>
                </button>
              );
            })}
          </div>
        ) : (
          <div className="h-48 flex items-center justify-center text-sm text-gray-400 italic">
            Categories feature coming soon...
          </div>
        )}
      </div>
    </aside >
  );
}
